<template>
  <div class="selection">
    <transition-group name="fade-fast-delay">
      <BaseSwiper
        v-show="!statsModalState"
        ref="swiper"
        :options="{ initialSlide, pagination: { el: pagination } }"
        key="1"
        @slide-change="currentIndex = $event">
        <template #header>
          <div class="navigation-container">
            <div class="navigation">
              <div class="main">
                <BaseTextAnimation2 class="alias" ref="alias">
                  <BaseOpponentLabel :opponent-label="currentOpponent && currentOpponent.alias || 'Placeholder'" :style="{opacity: currentOpponent && currentOpponent.alias ? 1 : 0}"/>
                </BaseTextAnimation2>
                <BaseTextAnimation3 class="name" ref="name">
                  <BaseText11 :text="currentOpponent ? currentOpponent.name : 'Stay tuned' | addLineBreaks"/>
                </BaseTextAnimation3>
                <div class="pagination" ref="pagination"/>
                <div class="arrows">
                  <BaseButtonArrow direction="left" @click.native="$refs['swiper'].swiper.slidePrev()"/>
                  <BaseButtonArrow direction="right" @click.native="$refs['swiper'].swiper.slideNext()"/>
                </div>
              </div>
              <transition-group name="fade-fast-delay">
                <BaseButtonOctagon
                  class="start-button"
                  text="Stare<br>down"
                  v-if="currentOpponent && opponentUnlockedStates[currentOpponent.id]"
                  @click.native.once="onOpponentSelected(currentOpponent.id)"
                  key="1"
                />
                <BaseLock
                  class="lock-button"
                  v-if="currentOpponent && !opponentUnlockedStates[currentOpponent.id]"
                  key="2"
                />
                <BaseButtonExpand
                  class="stats-button"
                  state="open"
                  v-if="currentOpponent"
                  @click.native="statsModalState = true"
                  key="3"
                />
              </transition-group>
            </div>
          </div>
        </template>
        <SwiperSlide v-for="(opponent, i) in opponents" :key="i">
          <div class="image-container">
            <div class="image-wrapper" ref="image-wrapper">
              <img v-if="opponentUnlockedStates[opponent.id]" class="image" :src="opponent.image1" data-depth="0.2">
              <img v-else class="image" :src="opponent.image4" data-depth="0.2">
            </div>
          </div>
        </SwiperSlide>
        <SwiperSlide>
          <ComingSoon/>
        </SwiperSlide>
      </BaseSwiper>
      <Stats
        class="stats"
        :opponent="currentOpponent" v-show="currentOpponent && statsModalState"
        @close="statsModalState = false"
        v-if="currentOpponent"
        key="2"
      />
    </transition-group>
  </div>
</template>

<script>
import Stats from './partials/Stats/Stats'
import ComingSoon from './partials/ComingSoon/ComingSoon'
import BaseSwiper from '@/components/BaseSwiper/BaseSwiper'
import BaseText11 from '@/components/BaseText11/BaseText11'
import BaseButtonArrow from '@/components/BaseButtonArrow/BaseButtonArrow'
import BaseButtonExpand from '@/components/BaseButtonExpand/BaseButtonExpand'
import BaseOpponentLabel from '@/components/BaseOpponentLabel/BaseOpponentLabel'
import BaseButtonOctagon from '@/components/BaseButtonOctagon/BaseButtonOctagon'
import BaseLock from '@/components/BaseLock/BaseLock'
import BaseTextAnimation2 from '@/components/BaseTextAnimation2/BaseTextAnimation2'
import BaseTextAnimation3 from '@/components/BaseTextAnimation3/BaseTextAnimation3'
import SwiperSlide from '@/components/BaseSwiper/components/SwiperSlide/SwiperSlide'
import { opponentIds, opponentsById } from '@/data/opponents'
import Parallax from 'parallax-js'
import { mapGetters, mapState } from 'vuex'
import { TimelineMax } from 'gsap'
export default {
  components: {
    Stats,
    BaseSwiper,
    ComingSoon,
    BaseText11,
    SwiperSlide,
    BaseButtonArrow,
    BaseButtonExpand,
    BaseOpponentLabel,
    BaseButtonOctagon,
    BaseLock,
    BaseTextAnimation2,
    BaseTextAnimation3
  },
  props: {
    initialSlide: {
      type: Number,
      default: 0
    }
  },
  mounted () {
    this.pagination = this.$refs['pagination']
    this.currentIndex = this.$refs['swiper'].realIndex
    this.$refs['image-wrapper'].forEach(el => new Parallax(el, { relativeInput: true, limitY: 0 }))
    this.tl = this.createTl()
  },
  data () {
    return {
      tl: null,
      pagination: null,
      currentIndex: null,
      statsModalState: false
    }
  },
  computed: {
    ...mapState('ui', [
      'faceCalibrationComplete'
    ]),
    ...mapGetters('user', [
      'opponentUnlockedStates',
      'userHasBeatenAllOpponents'
    ]),
    opponents () {
      return opponentIds.map(id => opponentsById[id])
    },
    currentOpponent () {
      return opponentsById[opponentIds[this.currentIndex]]
    }
  },
  watch: {
    currentOpponent () {
      this.$nextTick(() => {
        this.$refs['name'].createTl()
        this.tl.play(0)
      })
    }
  },
  methods: {
    onOpponentSelected (opponentId) {
      if (!this.opponentUnlockedStates[opponentId]) return
      this.$ga.event('opponent-selected', opponentId)
      this.$store.commit('gameplay/setSelectedOpponentId', opponentId)
      const nextRoute = this.faceCalibrationComplete ? '/gameplay' : '/calibration'
      if (localStorage.getItem('userHasSeenInstructions')) return this.$router.push(nextRoute)
      this.$router.push({ name: 'instructions', params: { nextRoute } })
    },
    onStatsOpen () {
      this.$refs['swiper'].swiper.allowTouchMove = false
    },
    onStatsClose () {
      this.$refs['swiper'].swiper.allowTouchMove = true
    },
    createTl () {
      const tl = new TimelineMax({ paused: false })
      tl.add([
        this.$refs['name'].tl,
        this.$refs['alias'].tl
      ], 0, 'start', 0)
      return tl
    }
  }
}
</script>

<style lang="scss" scoped>
.selection {
  width: 100%;
  height: 100%;
  display: flex;
  * {
    user-select: none;
  }
}
.navigation-container {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 50%;
  transform: translate(-50%);
  pointer-events: none;
  padding: var(--spacing-1) calc(var(--spacing-1) * 2);
  z-index: 2;
}
.navigation {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
}
.main {
  width: 100%;
  max-width: 20rem;
  text-align: left;
}
.name-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}
.name {
  white-space: nowrap;
}
.pagination {
  position: relative;
  width: 100%;
  margin: var(--spacing-1) 0;
  justify-content: flex-start;
}
.arrows {
  display: flex;
  > *:not(:last-child) { margin-right: 0.5rem }
  > * { pointer-events: all }
}
.image-container {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow: visible;
  pointer-events: none;
  z-index: -2;
}
.image-wrapper {
  position: absolute;
  height: 125%;
  right: -5%;
  bottom: -26.5%;
  display: block;
  transform: translateX(-50%);
}
.image {
  height: 100%;
  display: block;
}
.start-button,
.lock-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.start-button {
  pointer-events: all;
}
.stats-button {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: all;
}
::v-deep .swiper,
::v-deep .swiper-wrapper {
  height: 100%;
}
.stats {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}
</style>
